import { System } from '../../models/device.model';
import { SystemType } from '../../models/deploymentForm.model';

export interface AnnotatedSystem extends System {
  isProvisioned: boolean;
  isConflict: boolean;
}

export interface ProvisionStatus {
  systemId: string;
  isProvisioned: boolean;
  isConflict: boolean;
}

export function getCanonicalSystemTypeName(systemType: string | null | undefined): string {
  const normalizedSystemType = (systemType?.replace(/[\s()_-]/g, '') ?? '').toUpperCase();
  if (normalizedSystemType === 'ATPCAPS') {
    return 'Tempo-ATP';
  }

  return normalizedSystemType === 'CAPS' ? 'Tempo' : normalizedSystemType;
}

/**
 * Filters incoming systems by a search term (matched on machineNo or name) and annotates with
 * provision/conflict flags based on provisionedSystems and systemTypes metadata.
 * Returns filtered list plus a provision status payload for the currently selected system (or cleared state).
 */
export function filterAndAnnotateSystems(
  systems: any[],
  term: string,
  provisionedSystems: System[],
  systemTypes: { nameUpper: string; systemType: number }[],
  currentSystemType: SystemType | null | undefined,
  currentSelectedMachineNo: string | null | undefined
): { systems: AnnotatedSystem[]; provisionStatus: ProvisionStatus } {
  const loweredTerm = term?.toLowerCase() || '';

  const filtered: AnnotatedSystem[] = (systems || [])
    .filter(sys =>
      sys?.machineNo?.toLowerCase().includes(loweredTerm) ??
      sys?.name?.toLowerCase().includes(loweredTerm)
    )
    .map(sys => {
      const machineNoLower = sys.machineNo?.toLowerCase();
      const matchingSystems = (provisionedSystems || []).filter(p => p.systemId?.toLowerCase() === machineNoLower);

      // Conflict if ANY matching provisioned system has a recognized system type that maps to current systemType
      const isConflict = matchingSystems.some(existing => {
        const existingTypeNameUpper = getCanonicalSystemTypeName(existing.systemType);
        if (!existingTypeNameUpper) { return false; }
        const systemTypeEntry = systemTypes.find(mt =>
          getCanonicalSystemTypeName(mt.nameUpper) === existingTypeNameUpper
        );
        return !!systemTypeEntry && currentSystemType == systemTypeEntry.systemType;
      });

      return {
        ...sys,
        isProvisioned: matchingSystems.length > 0,
        isConflict
      } as AnnotatedSystem;
    });

  // Determine provision status to emit
  let provisionStatus: ProvisionStatus;
  if (!currentSelectedMachineNo) {
    provisionStatus = { systemId: '', isProvisioned: false, isConflict: false };
  } else {
    const selected = filtered.find(f => f.machineNo === currentSelectedMachineNo);
    provisionStatus = {
      systemId: currentSelectedMachineNo,
      isProvisioned: selected?.isProvisioned || false,
      isConflict: selected?.isConflict || false
    };
  }

  return { systems: filtered, provisionStatus };
}
